import styled, { css } from "styled-components";

export const TrackSubtitlesWrapperSC = styled.div<{ height?: number }>`
    --audio-player-width: 6em;
    --audio-track-width: 80px;
    --audio-time-width: 3.5em;
    z-index: 0;
    width: 100%;
    margin: auto;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    ${({ height }) =>
        height
            ? css`
                  height: ${height}px;
              `
            : css`
                  /*
                   * NOT height:100%.
                   *
                   * As a flex child of the page column (.b0, height:100dvh) this is meant to take
                   * the space the header and footer leave. A height of 100% instead resolves
                   * against the WHOLE column, so the block claims a full viewport of its own and
                   * the page grows past it — which is why the top panel scrolled away on an iPad
                   * while staying put in Chrome: Blink lets flex-basis win, WebKit honours the
                   * percentage. min-height:0 is the other half — without it a flex item refuses
                   * to shrink below its content, and the inner scroller never engages.
                   */
                  flex: 1 1 0;
                  min-height: 0;
              `}
`;

export const TrackSubtitlesContainerSC = styled.div`
    overflow-x: hidden;
    /* Not "overlay": a non-standard value Safari never implemented (Chrome now just aliases it
       to auto). Naming the real thing keeps the scroller a scroller on every browser. */
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    flex-direction: row;
    align-items: flex-start;
    height: 100%;
    display: flex;
    z-index: 1;
    width: 100%;
    position: relative;
    &::-webkit-scrollbar {
        width: 4px; /* vertical scrollbar */
        height: 4px; /* horizontal scrollbar */
    }

    &::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.4);
        border-radius: 4px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }
`;

export const VerticalTrackBackSC = styled.div<{ left?: string; right?: string }>`
    background-color: var(--gray);
    opacity: 0.5;
    mix-blend-mode: multiply;
    transition: opacity 0.2s;
    position: absolute;
    box-shadow:
        0 1px #ffffff96,
        inset 0 3px 8px #0000,
        inset 0 3px 11px -6px #000000b8;
    border-radius: 0;
    width: var(--audio-track-width);
    left: ${({ left }) => (left === "true" ? "var(--audio-time-width)" : "auto")};
    right: ${({ right }) => (right === "true" ? "var(--audio-time-width)" : "auto")};
    height: 100%;
`;

export const TrackContainerSC = styled.div`
    height: 100%;
    position: absolute;
`;

export const AllFragmentsContainerSC = styled.div<{
    height: string;
    left?: string;
    right?: string;
}>`
    display: flex;
    width: calc(50% - (var(--audio-player-width) / 2));
    height: ${({ height }) => height};
    padding-left: ${({ left }) => (left === "true" ? "var(--audio-time-width)" : 0)};
    padding-right: ${({ right }) => (right === "true" ? "var(--audio-time-width)" : 0)};
    flex: 0 1 auto;
    min-width: auto;
    position: relative;
    ${({ left, right }) => {
        if (right === "true") {
            return css`
                width: calc(var(--audio-track-width) + var(--audio-time-width));
            `;
        }
        if (left === "true") {
            return css`
                width: calc(
                    100% -
                        (var(--audio-player-width)) - var(--audio-time-width) - var(
                            --audio-track-width
                        )
                );
            `;
        }
    }}
`;

export const DeleteButtonWrapperSC = styled.div`
    position: fixed;
    width: 200px;
    height: 60px;
    top: 4em;
    left: 4em;
    z-index: 100;
`;

export const BulkActionToolbarSC = styled.div`
    position: fixed;
    bottom: 1em;
    right: 1em;
    z-index: 100;
    background: #fff;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    padding: 12px 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 280px;
`;

export const SelectAllWrapperSC = styled.div`
    position: absolute;
    top: -24px;
    right: var(--audio-time-width);
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: #666;
    & .ant-checkbox-inner {
        border-color: #1677ff;
    }
`;

/**
 * How a toolbar action looks.
 *
 * Shared because these buttons are rendered in two places now: the toolbar itself and the
 * settings panel (utils/settingsSlots). It used to live inside TopToolbarSC's block, and
 * portalling a button out of that subtree silently stripped its border, padding and pointer —
 * «Пометить русский готовым» came out looking like a stray line of text.
 */
export const toolbarButtonCss = css`
    .toolbar-btn {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 3px 12px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        border: 1px solid #d9d9d9;
        background: #fafafa;
        color: #333;
        transition: all 0.2s;
        user-select: none;
        white-space: nowrap;

        &:hover {
            border-color: #1677ff;
            color: #1677ff;
            background: #e6f4ff;
        }
    }
`;

export const TopToolbarSC = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    background: #fff;
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;

    ${toolbarButtonCss}
`;

export const SpeedControlButtonSC = styled.div<{ active?: boolean }>`
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    padding: 4px 4px 4px 0;

    .speed-toggle-btn {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 3px 12px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        border: 1px solid ${({ active }) => (active ? "#1677ff" : "#d9d9d9")};
        background: ${({ active }) => (active ? "#e6f4ff" : "#fafafa")};
        color: ${({ active }) => (active ? "#1677ff" : "#333")};
        transition: all 0.2s;
        user-select: none;
        white-space: nowrap;

        &:hover {
            border-color: #1677ff;
            color: #1677ff;
            background: #e6f4ff;
        }
    }
`;

export const SpeedControlPanelSC = styled.div`
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    background: #fff;
    border: 1px solid #1677ff;
    border-radius: 6px;
    padding: 4px 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    white-space: nowrap;

    .speed-label {
        font-size: 12px;
        font-weight: 600;
        color: #1677ff;
    }

    .speed-select-all {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 11px;
        color: #666;
    }

    .speed-slider-group {
        display: flex;
        align-items: center;
        gap: 4px;
        min-width: 140px;

        .ant-slider {
            flex: 1;
            margin: 0;
        }
    }

    .speed-value {
        font-size: 12px;
        font-weight: 600;
        min-width: 24px;
        text-align: center;
    }

    .speed-actions {
        display: flex;
        gap: 4px;
    }

    .speed-close {
        cursor: pointer;
        color: #999;
        font-size: 14px;
        padding: 0 2px;
        &:hover {
            color: #333;
        }
    }
`;
